import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, Plus, Brain, BarChart3,
  CalendarClock, Bell, Gauge, Star, Mail,
  TrendingUp, TrendingDown, AlertTriangle, BellRing,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------------- */
/*  Content                                                                */
/* ----------------------------------------------------------------------- */

const PARTNERS = ['NASDAQ', 'CME GROUP', 'BLOOMBERG', 'FEDERAL RESERVE', 'LSE GROUP', 'REUTERS'];

const TESTIMONIALS = [
  {
    quote: 'TELOS is the first platform that made macro actually usable for me. I check the economic calendar and sentiment gauge every morning before I trade.',
    name: 'Adel D.', role: 'FX & Indices Trader', initials: 'AD',
  },
  {
    quote: 'I avoided fundamentals for years. TELOS changes that — the news playbooks tell me exactly what to expect before a big release and how to position.',
    name: 'Vigneshwar S.', role: 'Futures Trader', initials: 'VS',
  },
  {
    quote: 'The stock research tool alone is worth it. I search any ticker and instantly get fundamentals, analyst ratings, and key metrics — saves me hours every week.',
    name: 'Karan U.', role: 'Forex Trader', initials: 'KU',
  },
];

const PRICING_FEATURES = [
  'Live Market Prices & AI Sentiment Gauge',
  'Economic Calendar with AI Playbooks',
  'Real-Time News & Headlines Feed',
  'Interactive Chart Analysis',
  'AI Chat Assistant',
  'Global Market & Risk Overview',
];

const FAQS = [
  {
    q: 'What is TELOS?',
    a: 'TELOS is an AI-powered fundamental analysis platform built for traders. It gives you live market prices, economic calendar events with AI playbooks, real-time news, interactive chart analysis, and deep stock research — all in one dashboard.',
  },
  {
    q: 'What edge does TELOS give traders?',
    a: 'TELOS bridges the gap between technical and fundamental analysis. The platform provides real-time news playbooks, instant headline alerts, candlestick-level market context, and AI-powered sentiment analysis so you can understand why markets move and position yourself accordingly.',
  },
  {
    q: 'Who is TELOS designed for?',
    a: 'TELOS is designed for technical traders who want to incorporate fundamentals into their trading without spending hours on research. Whether you trade forex, indices, futures, or stocks, TELOS gives you the macro context you need to trade with more confidence.',
  },
  {
    q: 'What is TELOS’s mission?',
    a: 'Our mission is to democratize institutional-grade market intelligence. We believe every trader deserves access to the same data and insights that hedge funds and institutional traders use, presented in a way that’s easy to understand and act on.',
  },
  {
    q: 'What types of AI models does the platform use?',
    a: 'We use a combination of advanced NLP models for news analysis and sentiment detection, time-series models for market data analysis, and large language models for generating clear, actionable insights from complex financial data.',
  },
  {
    q: 'What kind of support do you offer?',
    a: 'Once you’re signed in, use the Support & Feedback button in the sidebar to send us a message — we read every one and reply by email, usually within 24 hours.',
  },
  {
    q: 'Can I cancel my subscription anytime?',
    a: 'Yes, you can cancel at any time. Your access remains active until the end of your current billing period. There are no cancellation fees or long-term commitments.',
  },
  {
    q: 'Do you provide refunds?',
    a: 'All sales are final and we do not offer refunds. We encourage you to take advantage of any available trial period to ensure TELOS is the right fit for your trading before committing.',
  },
];

/* Shared scroll-in animation */
const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
};

/* ----------------------------------------------------------------------- */
/*  Page                                                                   */
/* ----------------------------------------------------------------------- */

export function Landing() {
  return (
    <div className="min-h-screen bg-bg text-fg overflow-x-hidden">
      <Header />
      <Hero />
      <Partners />
      <Testimonials />
      <FeatureSections />
      <Pricing />
      <Faq />
      <CtaSection />
      <Footer />
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  Header                                                                 */
/* ----------------------------------------------------------------------- */

function Header() {
  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-bg/80 backdrop-blur-lg">
      <div className="mx-auto max-w-6xl px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2">
          <Logo />
          <span className="font-bold text-base tracking-wide">TELOS</span>
        </a>
        <nav className="hidden md:flex items-center gap-7 text-xs font-medium text-fg-muted">
          <a href="#features" className="hover:text-fg transition-colors">Features</a>
          <a href="#testimonials" className="hover:text-fg transition-colors">Testimonials</a>
          <a href="#pricing" className="hover:text-fg transition-colors">Pricing</a>
          <a href="#faq" className="hover:text-fg transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link to="/signin" className="hidden sm:block">
            <Button variant="ghost" size="sm">Sign in</Button>
          </Link>
          <Link to="/signup"><Button size="sm">Get started</Button></Link>
        </div>
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------------- */
/*  Hero                                                                   */
/* ----------------------------------------------------------------------- */

function Hero() {
  return (
    <section id="top" className="relative px-6 lg:px-8 pt-16 lg:pt-24 pb-16">
      {/* Atmospheric background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/5 size-[30rem] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute top-0 right-1/5 size-80 rounded-full bg-cyan-500/12 blur-3xl" />
        <div className="absolute top-44 left-1/2 size-72 -translate-x-1/2 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-3xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 text-2xs font-semibold">
            <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse-soft" />
            Real-time market intelligence for serious traders
          </div>
          <h1 className="mt-6 text-[2rem] sm:text-4xl lg:text-[3.5rem] font-bold leading-[1.35] tracking-tight">
            Know why markets{' '}
            <span className="bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500 bg-clip-text text-transparent">
              move before you trade
            </span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-fg-muted max-w-xl mx-auto leading-relaxed">
            Live prices, AI sentiment, economic calendar, and market news — all in one dashboard built for traders who care about fundamentals as much as setups.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="group">
                Get started <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <a href="#features"><Button variant="secondary" size="lg">See features</Button></a>
          </div>

          {/* Trusted by */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="flex -space-x-2">
              {['JD', 'RJ', 'JS', 'ED', 'TD', 'DA'].map((a) => (
                <div
                  key={a}
                  className="size-7 rounded-full bg-bg-elevated border-2 border-bg flex items-center justify-center text-[0.55rem] font-bold text-fg-muted"
                >
                  {a}
                </div>
              ))}
            </div>
            <span className="text-xs text-fg-muted">
              Trusted by <strong className="text-fg">1,000+</strong> traders
            </span>
          </div>
        </motion.div>
      </div>

      {/* Dashboard mockup */}
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative mx-auto mt-14 max-w-4xl"
      >
        <DashboardMockup />
      </motion.div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Partners marquee                                                       */
/* ----------------------------------------------------------------------- */

function Partners() {
  /* Render the base list 4× so the track is always wider than the viewport.
     Animating x from 0% to -25% shifts the track by exactly one base block,
     so the wrap point is invisible — a seamless, gap-free infinite loop. */
  const REPEAT = 4;
  const track = Array.from({ length: REPEAT }, () => PARTNERS).flat();
  return (
    <section className="border-y border-border/60 py-10 bg-bg-subtle/40">
      <p className="text-center text-2xs font-semibold uppercase tracking-[0.18em] text-fg-subtle">
        Powered by institutional-grade data
      </p>
      <div className="relative mt-6 overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_12%,#000_88%,transparent)]">
        <motion.div
          className="flex w-max gap-14 pr-14 whitespace-nowrap"
          animate={{ x: ['0%', `-${100 / REPEAT}%`] }}
          transition={{ duration: 28, ease: 'linear', repeat: Infinity }}
        >
          {track.map((p, i) => (
            <span
              key={`${p}-${i}`}
              className="text-sm font-bold tracking-[0.14em] text-fg-subtle/70"
            >
              {p}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Testimonials                                                           */
/* ----------------------------------------------------------------------- */

function Testimonials() {
  return (
    <section id="testimonials" className="px-6 lg:px-8 py-24">
      <div className="mx-auto max-w-6xl">
        <SectionHeader tag="Testimonials" title="What traders are saying"
          subtitle="Real feedback from traders who switched from pure technical analysis." />
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name} {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="p-6 rounded-xl bg-bg-card border border-border card-highlight flex flex-col"
            >
              <div className="flex gap-0.5 text-warn">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="size-3.5 fill-current" />
                ))}
              </div>
              <p className="mt-4 text-sm text-fg-muted leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="size-9 rounded-full bg-accent/15 border border-accent/25 flex items-center justify-center text-2xs font-bold text-accent">
                  {t.initials}
                </div>
                <div>
                  <div className="text-xs font-semibold">{t.name}</div>
                  <div className="text-2xs text-fg-subtle">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Feature sections                                                       */
/* ----------------------------------------------------------------------- */

function FeatureSections() {
  const features = [
    {
      tag: 'Economic Calendar', icon: CalendarClock,
      tone: 'text-warn', toneSoft: 'bg-warn/15', toneOrb: 'bg-warn/25',
      title: 'Stop avoiding red folder news, start to trade it',
      body: 'Be one of the few who know how to trade the news with accuracy. TELOS breaks down every major economic release with clear playbooks showing what to expect and how to react.',
      bullets: [
        'Pre-event analysis with min/max forecasts',
        'Real-time sentiment shifts during releases',
        'Historical reaction patterns for each event type',
      ],
      cta: 'See news playbook', mockup: <CalendarMockup />, reverse: false,
    },
    {
      tag: 'Instant Alerts', icon: Bell,
      tone: 'text-bear', toneSoft: 'bg-bear/15', toneOrb: 'bg-bear/25',
      title: 'Avoid getting stopped out by headlines with instant alerts',
      body: 'Most traders sit it out. TELOS shows you what the market expects and how to react — with real-time alerts on high-impact news events before they move the market.',
      bullets: [
        'Instant notifications on breaking news',
        'Market-impact assessment for each headline',
        'Curated feed filtered to what actually moves price',
      ],
      cta: 'See live alerts', mockup: <AlertMockup />, reverse: true,
    },
    {
      tag: 'AI Analysis', icon: Gauge,
      tone: 'text-bull', toneSoft: 'bg-bull/15', toneOrb: 'bg-bull/25',
      title: 'Know your market bias before you open a single chart',
      body: 'TELOS synthesises economic data, central-bank policy, and market sentiment into a clear, actionable trading bias for every major pair — so you trade with conviction, not guesswork.',
      bullets: [
        'Bull and bear case laid out side by side',
        'Updated after every major economic release',
        'Covers major forex pairs, indices and commodities',
      ],
      cta: 'See today’s bias', mockup: <BiasMockup />, reverse: false,
    },
  ];

  return (
    <section id="features" className="px-6 lg:px-8 py-12">
      <div className="mx-auto max-w-6xl space-y-24 py-12">
        {features.map((f) => (
          <div
            key={f.tag}
            className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
          >
            <motion.div {...fadeUp} className={cn(f.reverse && 'lg:order-2')}>
              <span className={cn('inline-flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide', f.tone)}>
                <f.icon className="size-3.5" /> {f.tag}
              </span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
                {f.title}
              </h2>
              <p className="mt-4 text-sm text-fg-muted leading-relaxed">{f.body}</p>
              <ul className="mt-6 space-y-2.5">
                {f.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm">
                    <span className={cn('mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full', f.toneSoft, f.tone)}>
                      <Check className="size-2.5" strokeWidth={3} />
                    </span>
                    <span className="text-fg-muted">{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="mt-7 inline-block">
                <Button className="group">
                  {f.cta} <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className={cn('relative', f.reverse && 'lg:order-1')}
            >
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute -z-10 -top-10 -right-8 size-60 rounded-full blur-3xl opacity-70',
                  f.toneOrb,
                )}
              />
              {f.mockup}
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Pricing                                                                */
/* ----------------------------------------------------------------------- */

function Pricing() {
  const [annual, setAnnual] = useState(true);
  const price = annual ? 24 : 29;

  return (
    <section id="pricing" className="px-6 lg:px-8 py-24 bg-bg-subtle/40 border-y border-border/60">
      <div className="mx-auto max-w-6xl">
        <SectionHeader tag="Pricing" title="One plan, everything included"
          subtitle="No tiers, no add-ons. Everything TELOS has, from day one." />

        {/* Toggle */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <span className={cn('text-xs font-medium transition-colors', !annual ? 'text-fg' : 'text-fg-subtle')}>
            Monthly
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={annual}
            onClick={() => setAnnual((v) => !v)}
            aria-label="Toggle billing period"
            className={cn(
              'relative h-6 w-12 shrink-0 rounded-full transition-colors',
              annual ? 'bg-accent' : 'bg-border-strong',
            )}
          >
            <span
              className={cn(
                'absolute top-1 size-4 rounded-full bg-bg shadow-sm transition-all duration-200',
                annual ? 'left-7' : 'left-1',
              )}
            />
          </button>
          <div className="flex items-center gap-2">
            <span className={cn('text-xs font-medium transition-colors', annual ? 'text-fg' : 'text-fg-subtle')}>
              Annual
            </span>
            <span className="rounded-full bg-bull/15 text-bull text-2xs font-bold px-2 py-0.5">
              Save 17%
            </span>
          </div>
        </div>

        {/* Card */}
        <motion.div
          {...fadeUp}
          className="relative mx-auto mt-10 max-w-sm rounded-2xl bg-bg-card border border-border card-highlight glow-accent p-8"
        >
          <div className="text-2xs font-semibold uppercase tracking-wide text-accent">Premium</div>
          <div className="mt-3 flex items-end gap-1.5">
            {annual && <span className="text-lg text-fg-subtle line-through">£29</span>}
            <span className="text-5xl font-bold tracking-tight tabular">£{price}</span>
            <span className="mb-1 text-sm text-fg-muted">/month</span>
          </div>
          <div className="mt-1 text-2xs text-fg-subtle h-4">
            {annual ? 'Billed annually at £289/year' : 'Billed monthly, cancel anytime'}
          </div>

          <ul className="mt-6 space-y-3">
            {PRICING_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm">
                <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <Check className="size-2.5" strokeWidth={3} />
                </span>
                <span className="text-fg-muted">{f}</span>
              </li>
            ))}
          </ul>

          <Link to="/signup" className="mt-8 block">
            <Button size="lg" className="w-full">{annual ? "Get Pro Yearly" : "Get Pro Monthly"}</Button>
          </Link>
          <p className="mt-3 text-center text-2xs text-fg-subtle">
            Cancel anytime · No long-term commitment
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  FAQ                                                                    */
/* ----------------------------------------------------------------------- */

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="px-6 lg:px-8 py-24">
      <div className="mx-auto max-w-3xl">
        <SectionHeader tag="FAQ" title="Frequently asked questions"
          subtitle="Everything you need to know about TELOS." />
        <div className="mt-10 space-y-2.5">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className="rounded-xl bg-bg-card border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="text-sm font-semibold">{f.q}</span>
                  <Plus
                    className={cn(
                      'size-4 shrink-0 text-accent transition-transform duration-200',
                      isOpen && 'rotate-45',
                    )}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-4 text-sm text-fg-muted leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  CTA                                                                    */
/* ----------------------------------------------------------------------- */

function CtaSection() {
  return (
    <section className="px-6 lg:px-8 py-24">
      <motion.div
        {...fadeUp}
        className="relative mx-auto max-w-4xl overflow-hidden rounded-2xl border border-border mesh-hero px-8 py-16 text-center"
      >
        <div aria-hidden className="absolute -top-20 -right-16 size-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div aria-hidden className="absolute -bottom-24 -left-16 size-72 rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="relative">
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">
            Elevate your trading{' '}
            <span className="bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500 bg-clip-text text-transparent">
              today
            </span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-fg-muted">
            Get institutional-grade market analysis at your fingertips.
          </p>
          <Link to="/signup" className="mt-8 inline-block">
            <Button size="lg" className="group">
              Unlock market intelligence
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>
      </motion.div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Footer                                                                 */
/* ----------------------------------------------------------------------- */

function Footer() {
  return (
    <footer className="border-t border-border/60 px-6 lg:px-8 py-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="font-bold text-base tracking-wide">TELOS</span>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-fg-muted">
            <a href="#features" className="hover:text-fg transition-colors">Features</a>
            <a href="#pricing" className="hover:text-fg transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-fg transition-colors">FAQ</a>
            <Link to="/signin" className="hover:text-fg transition-colors">Sign in</Link>
            <a
              href="mailto:support@telosintelligence.ai"
              className="inline-flex items-center gap-1.5 hover:text-fg transition-colors"
            >
              <Mail className="size-3.5" /> Contact
            </a>
          </nav>
        </div>
        <div className="mt-8 pt-6 border-t border-border/60 text-2xs text-fg-subtle">
          © {new Date().getFullYear()} TELOS Intelligence. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------------------------------------------------- */
/*  Shared bits                                                            */
/* ----------------------------------------------------------------------- */

function SectionHeader({ tag, title, subtitle }: { tag: string; title: string; subtitle: string }) {
  return (
    <motion.div {...fadeUp} className="text-center">
      <div className="inline-block rounded-full bg-accent/10 border border-accent/25 text-accent text-2xs font-semibold uppercase tracking-wide px-3 py-1">
        {tag}
      </div>
      <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
      <p className="mt-3 text-sm text-fg-muted max-w-lg mx-auto">{subtitle}</p>
    </motion.div>
  );
}

function Logo() {
  return (
    <svg width="22" height="22" viewBox="8 8 60 60" fill="none" className="text-accent">
      <circle cx="38" cy="38" r="30" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.3" />
      <circle cx="38" cy="38" r="12" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="38" cy="38" r="4.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="38" cy="38" r="1.8" fill="currentColor" />
      <line x1="38" y1="8" x2="38" y2="24" stroke="currentColor" strokeWidth="1.2" />
      <line x1="38" y1="52" x2="38" y2="68" stroke="currentColor" strokeWidth="1.2" />
      <line x1="8" y1="38" x2="24" y2="38" stroke="currentColor" strokeWidth="1.2" />
      <line x1="52" y1="38" x2="68" y2="38" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

/* Browser-style frame wrapper for all mockups */
function MockFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-bg-card border border-border shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-bg-subtle/60">
        <span className="flex gap-1.5">
          <span className="size-2 rounded-full bg-border-strong" />
          <span className="size-2 rounded-full bg-border-strong" />
          <span className="size-2 rounded-full bg-border-strong" />
        </span>
        <span className="ml-2 text-2xs text-fg-subtle">{label}</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-bull/15 text-bull text-[0.55rem] font-bold px-2 py-0.5">
          <span className="size-1.5 rounded-full bg-bull animate-pulse-soft" /> LIVE
        </span>
      </div>
      {children}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  Mockups                                                                */
/* ----------------------------------------------------------------------- */

function DashboardMockup() {
  return (
    <MockFrame label="app.telosintelligence.ai">
      <div className="grid grid-cols-[48px_1fr] sm:grid-cols-[48px_1fr_196px] h-[284px] sm:h-[324px]">
        {/* Sidebar */}
        <div className="border-r border-border bg-bg-subtle/50 flex flex-col items-center gap-2.5 py-3">
          {[
            { Icon: BarChart3, active: true },
            { Icon: CalendarClock, active: false },
            { Icon: Bell, active: false },
            { Icon: Gauge, active: false },
            { Icon: Brain, active: false },
          ].map(({ Icon, active }, i) => (
            <span
              key={i}
              className={cn(
                'flex size-7 items-center justify-center rounded-lg',
                active ? 'bg-accent text-[rgb(20_15_8)]' : 'text-fg-subtle',
              )}
            >
              <Icon className="size-3.5" />
            </span>
          ))}
        </div>

        {/* Chart */}
        <div className="p-4 flex flex-col min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold">EUR/USD</div>
              <div className="text-2xs text-fg-subtle tabular">
                1.0847 <span className="text-bull font-semibold">▲ +0.32%</span>
              </div>
            </div>
            <div className="flex gap-1">
              {['1H', '4H', '1D'].map((tf, i) => (
                <span
                  key={tf}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[0.55rem] font-semibold',
                    i === 1 ? 'bg-accent text-[rgb(20_15_8)]' : 'text-fg-subtle',
                  )}
                >
                  {tf}
                </span>
              ))}
            </div>
          </div>
          <svg viewBox="0 0 300 120" className="mt-3 flex-1 w-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lg-hero" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--bull))" stopOpacity="0.32" />
                <stop offset="100%" stopColor="rgb(var(--bull))" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0,90 C30,80 50,55 80,60 C110,65 130,30 165,38 C200,46 220,18 255,26 C275,30 290,20 300,16 L300,120 L0,120 Z"
              fill="url(#lg-hero)"
            />
            <path
              d="M0,90 C30,80 50,55 80,60 C110,65 130,30 165,38 C200,46 220,18 255,26 C275,30 290,20 300,16"
              fill="none" stroke="rgb(var(--bull))" strokeWidth="2.5" strokeLinecap="round"
            />
          </svg>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="rounded bg-bull/15 text-bull px-2 py-1 text-[0.55rem] font-semibold">Bias: Bullish</span>
            <span className="rounded bg-accent/15 text-accent px-2 py-1 text-[0.55rem] font-semibold">Sentiment 72</span>
            <span className="rounded bg-warn/15 text-warn px-2 py-1 text-[0.55rem] font-semibold">CPI in 2h</span>
          </div>
        </div>

        {/* Right column — sentiment gauge + news */}
        <div className="border-l border-border bg-bg-subtle/40 p-3 hidden sm:flex flex-col gap-3">
          <div className="rounded-lg bg-bg-card border border-border p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[0.55rem] font-semibold uppercase tracking-wide text-fg-subtle">
                Market Sentiment
              </span>
              <span className="text-[0.6rem] font-bold text-bull">72 · Bullish</span>
            </div>
            <div className="mt-2 relative h-1.5 rounded-full bg-gradient-to-r from-bear via-warn to-bull">
              <span
                className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg ring-2 ring-bg-card"
                style={{ left: '72%' }}
              />
            </div>
          </div>
          <div>
            <div className="text-2xs font-semibold uppercase tracking-wide text-fg-subtle">Live News</div>
            <div className="mt-2 space-y-1.5">
              {[
                { t: 'Fed holds rates steady', tone: 'bull', tag: 'Bullish' },
                { t: 'US CPI beats forecast', tone: 'bear', tag: 'Bearish' },
                { t: 'ECB signals caution', tone: 'warn', tag: 'Watch' },
              ].map((n) => (
                <div key={n.t} className="rounded-lg bg-bg-card border border-border p-2">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'rounded px-1 py-0.5 text-[0.5rem] font-bold',
                        n.tone === 'bull' && 'bg-bull/15 text-bull',
                        n.tone === 'bear' && 'bg-bear/15 text-bear',
                        n.tone === 'warn' && 'bg-warn/15 text-warn',
                      )}
                    >
                      {n.tag}
                    </span>
                    <span className="ml-auto text-[0.5rem] text-fg-subtle">2m</span>
                  </div>
                  <div className="mt-1 text-[0.6rem] leading-snug text-fg-muted">{n.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MockFrame>
  );
}

function CalendarMockup() {
  const rows = [
    { time: 'Fri 8:30', evt: '🇺🇸 US Non-Farm Payrolls', impact: 'high', fc: '185K', act: '272K', up: true },
    { time: 'Fri 8:30', evt: '🇺🇸 US Unemployment Rate', impact: 'high', fc: '3.9%', act: '3.8%', up: true },
    { time: 'Fri 10:00', evt: '🇺🇸 ISM Manufacturing PMI', impact: 'medium', fc: '49.5', act: '49.2', up: false },
    { time: 'Mon 9:30', evt: '🇬🇧 UK CPI YoY', impact: 'high', fc: '2.9%', act: '2.8%', up: true },
  ];
  const cols = 'grid-cols-[52px_1fr_44px_38px_38px]';
  return (
    <MockFrame label="Institutional Economic Calendar">
      <div className="p-3 text-xs">
        <div className={cn('grid gap-2 px-2 pb-2 text-[0.5rem] font-semibold uppercase tracking-wide text-fg-subtle', cols)}>
          <span>Time</span><span>Event</span><span>Impact</span>
          <span className="text-right">Fc</span><span className="text-right">Actual</span>
        </div>
        {rows.map((r, i) => (
          <div
            key={i}
            className={cn('grid gap-2 items-center rounded-lg px-2 py-2', cols, i % 2 === 0 && 'bg-bg-subtle/50')}
          >
            <span className="text-[0.58rem] text-fg-subtle">{r.time}</span>
            <span className="text-[0.64rem] text-fg truncate">{r.evt}</span>
            <span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-[0.5rem] font-bold uppercase',
                  r.impact === 'high' ? 'bg-bear/15 text-bear' : 'bg-warn/15 text-warn',
                )}
              >
                {r.impact}
              </span>
            </span>
            <span className="text-right text-[0.6rem] text-fg-muted tabular">{r.fc}</span>
            <span
              className={cn(
                'text-right text-[0.64rem] font-bold tabular',
                r.up === true ? 'text-bull' : r.up === false ? 'text-bear' : 'text-fg-muted',
              )}
            >
              {r.act}
            </span>
          </div>
        ))}
        {/* AI playbook callout */}
        <div className="mt-2 rounded-lg border border-accent/25 bg-accent/5 p-2.5">
          <div className="flex items-center gap-1.5 text-[0.55rem] font-bold uppercase tracking-wide text-accent">
            <Brain className="size-3" /> AI Playbook · Non-Farm Payrolls
          </div>
          <p className="mt-1 text-[0.62rem] leading-snug text-fg-muted">
            Strong beat (272K vs 185K) — bullish for USD. Expect EUR/USD pressure into the
            close; fade rallies while the dollar bid holds.
          </p>
        </div>
      </div>
    </MockFrame>
  );
}

function AlertMockup() {
  const alerts = [
    { tag: 'HIGH IMPACT', tone: 'bear', title: 'US CPI release in 15 minutes', note: 'Forecast 3.1% YoY · expect USD volatility', time: 'now' },
    { tag: 'BREAKING', tone: 'warn', title: 'Fed officials signal patience on cuts', note: 'Hawkish tone — watch DXY and gold', time: '4m' },
    { tag: 'RELEASED', tone: 'bull', title: 'UK retail sales beat expectations', note: '+1.2% vs +0.4% fc — GBP bid', time: '22m' },
  ];
  return (
    <div className="relative">
      {/* Push notification — pops out of the frame */}
      <motion.div
        initial={{ opacity: 0, y: -18, scale: 0.9 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="absolute -top-7 -right-2 sm:-right-6 z-20 w-[17rem] rounded-xl bg-bg-elevated border border-bear/40 shadow-[0_24px_52px_-12px_rgba(0,0,0,0.65)] p-3.5"
      >
        <div className="flex items-start gap-3">
          <span className="relative flex size-8 shrink-0 items-center justify-center rounded-lg bg-bear/15 text-bear">
            <BellRing className="size-4" />
            <span className="absolute -inset-px rounded-lg ring-2 ring-bear/40 animate-pulse-soft" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold">TELOS Alert</span>
              <span className="rounded bg-bear/15 text-bear text-[0.5rem] font-bold px-1 py-0.5">NEW</span>
              <span className="ml-auto text-[0.55rem] text-fg-subtle">now</span>
            </div>
            <p className="mt-1 text-[0.68rem] leading-snug text-fg-muted">
              🔴 High-impact: US CPI drops in 15 min — expect USD volatility.
            </p>
          </div>
        </div>
      </motion.div>

      <MockFrame label="Live Headline Alerts">
        {/* Notifications toggle */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-bg-subtle/40">
          <Bell className="size-3 text-accent" />
          <span className="text-[0.6rem] font-medium text-fg-muted">Push notifications</span>
          <span className="ml-auto flex items-center gap-1.5">
            <span className="text-[0.55rem] font-bold text-bull">ON</span>
            <span className="relative h-3.5 w-6 rounded-full bg-bull/30">
              <span className="absolute right-0.5 top-0.5 size-2.5 rounded-full bg-bull" />
            </span>
          </span>
        </div>
        <div className="p-3 space-y-2.5">
          {alerts.map((a) => (
            <div key={a.title} className="rounded-lg bg-bg-subtle/60 border border-border p-3">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[0.5rem] font-bold tracking-wide',
                    a.tone === 'bear' && 'bg-bear/15 text-bear',
                    a.tone === 'warn' && 'bg-warn/15 text-warn',
                    a.tone === 'bull' && 'bg-bull/15 text-bull',
                  )}
                >
                  {a.tag}
                </span>
                <span className="ml-auto text-[0.55rem] text-fg-subtle">{a.time}</span>
              </div>
              <div className="mt-1.5 text-xs font-semibold text-fg">{a.title}</div>
              <div className="mt-0.5 text-[0.65rem] text-fg-muted">{a.note}</div>
            </div>
          ))}
        </div>
      </MockFrame>
    </div>
  );
}

function BiasMockup() {
  const higher = [
    { title: 'Hawkish ECB hold', reason: 'A surprise hawkish tone widens the EUR–USD rate spread and lifts the euro.' },
    { title: 'Cooling US inflation', reason: 'A soft CPI print revives Fed rate-cut bets and pressures the dollar.' },
  ];
  const lower = [
    { title: 'Strong US payrolls', reason: 'An upside NFP surprise firms the USD bid and pushes back the cut timeline.' },
    { title: 'Risk-off shock', reason: 'An equity drawdown drives safe-haven flows straight into the dollar.' },
  ];
  return (
    <MockFrame label="AI Trading Bias — EUR/USD">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">EUR/USD</span>
          <span className="rounded-full bg-bull/15 text-bull text-2xs font-bold px-2.5 py-0.5">
            Bullish bias
          </span>
        </div>
        {/* Conviction bar */}
        <div className="mt-3">
          <div className="flex justify-between text-[0.55rem] text-fg-subtle">
            <span>Bearish</span>
            <span className="font-semibold text-bull">Conviction 68%</span>
            <span>Bullish</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-gradient-to-r from-bear/25 via-warn/25 to-bull/25 overflow-hidden">
            <div className="h-full w-[68%] rounded-full bg-bull" />
          </div>
        </div>
        {/* What could flip this bias — fundamental catalysts */}
        <div className="mt-4 flex items-center gap-1.5">
          <AlertTriangle className="size-3.5 text-warn" />
          <span className="text-2xs font-bold uppercase tracking-wide">What could flip this bias</span>
        </div>
        <div className="mt-2 space-y-2">
          <FlipCol tone="bull" label="Could push higher" items={higher} />
          <FlipCol tone="bear" label="Could push lower" items={lower} />
        </div>
      </div>
    </MockFrame>
  );
}

function FlipCol({
  tone, label, items,
}: {
  tone: 'bull' | 'bear';
  label: string;
  items: { title: string; reason: string }[];
}) {
  const Icon = tone === 'bull' ? TrendingUp : TrendingDown;
  return (
    <div
      className={cn(
        'rounded-lg border p-2.5',
        tone === 'bull' ? 'border-bull/30 bg-bull/5' : 'border-bear/30 bg-bear/5',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 text-[0.55rem] font-bold uppercase tracking-wide',
          tone === 'bull' ? 'text-bull' : 'text-bear',
        )}
      >
        <Icon className="size-3" /> {label}
      </div>
      <ul className="mt-1.5 space-y-1.5">
        {items.map((f) => (
          <li key={f.title}>
            <div className="text-[0.64rem] font-semibold text-fg">{f.title}</div>
            <div className="text-[0.58rem] leading-snug text-fg-muted">{f.reason}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
